import type { Product } from "@models/product";
import type { ShoppingCartItem } from "@models/shoppingCart";

/**
 * Whether no live vendor offers the line's sku (PO ruling 2026-11-02: it can't be
 * ordered), read from the line's product (GET /products/{id}): the product's own
 * `hasOffers`, or the line's variant's. False while the product is unknown (not read,
 * or its read failed) and for a payload without the flag. False for a restricted
 * product too: its read blanks `hasOffers` with the prices (#228), and that line is the
 * restriction's to explain, not stock's.
 */
export const lineHasNoOffers = (
    item: ShoppingCartItem,
    product: Product | undefined,
): boolean => {
    if (!product || product.restriction?.isRestricted) {
        return false;
    }

    if (product.hasOffers === false) {
        return true;
    }

    const variantId = item.variant?.variantId;

    return (
        variantId !== undefined &&
        (product.variants ?? []).find((v) => v.id === variantId)?.hasOffers ===
            false
    );
};

/**
 * The lines the cart's figures price: every line but those no live vendor offers. Such a
 * line's `price` is its dead offer's, so it is left out of the subtotal (and with it the
 * free-shipping meter, the shipping and card-fee quotes and the order total), as a
 * restricted line is (#228). Cart and Checkout feed this to `shippableSubtotal` and
 * `shippableUnitCount`.
 */
export const offeredLines = (
    items: ShoppingCartItem[],
    products: Map<string, Product>,
): ShoppingCartItem[] =>
    items.filter((item) => !lineHasNoOffers(item, products.get(item.productId)));

/** How many lines no live vendor offers: Cart and Checkout close the way to an order on it. */
export const noOfferLineCount = (
    items: ShoppingCartItem[],
    products: Map<string, Product>,
): number =>
    items.filter((item) => lineHasNoOffers(item, products.get(item.productId)))
        .length;
